import { Button, DataTable, Input, SegmentedControl } from "@design";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Ban, CircleCheck, Search, Trash2, UserPlus, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { adminListUsersOptions, type UserResponse } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { RowMenu } from "@/components/build/RowMenu";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { useUserColumns } from "./userColumns";
import { canDeleteUser, canManageUser } from "./userRules";
import { useUserActions } from "./useUserActions";

const PAGE_SIZE = 20;

/** /admin/users: everyone with access, paged on the server (sorted by name). */
export function UsersPage() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const canEdit = useAdminCanEdit();
  const me = useCurrentUser().data?.user;
  const meRef = { uuid: me?.uuid ?? "", role: me?.role ?? "member" };

  const [search, setSearch] = useState("");
  const [show, setShow] = useState("active");
  const [page, setPage] = useState(1);
  const term = useDebouncedValue(search.trim());

  const users = useQuery({
    ...adminListUsersOptions({
      query: { page, page_size: PAGE_SIZE, search: term || undefined, include_inactive: show === "all" },
    }),
    placeholderData: keepPreviousData,
  });
  const actions = useUserActions();
  const columns = useUserColumns({ me: meRef, canEdit, onRoleChange: actions.changeRole });

  const rowMenu = (user: UserResponse) => {
    if (!canManageUser(user, meRef)) return null;
    const name = user.name || user.email;
    const items = user.isActive
      ? [
          {
            label: t("users.menu.deactivate"),
            icon: <Ban size={16} />,
            danger: true,
            onSelect: () => actions.request(user, "deactivate"),
          },
        ]
      : [
          {
            label: t("users.menu.activate"),
            icon: <CircleCheck size={16} />,
            onSelect: () => actions.request(user, "activate"),
          },
        ];
    if (canDeleteUser(user)) {
      items.push({
        label: t("users.menu.delete"),
        icon: <Trash2 size={16} />,
        danger: true,
        onSelect: () => actions.request(user, "delete"),
      });
    }
    return <RowMenu name={name} items={items} />;
  };

  const total = users.data?.meta.total ?? 0;
  return (
    <AdminPage
      title={t("users.title")}
      subtitle={t("users.subtitle")}
      actions={
        <Button variant="primary" leadingIcon={<UserPlus size={16} />} onClick={() => navigate("/admin/users/new")}>
          {t("users.add")}
        </Button>
      }
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="md:max-w-xs md:flex-1">
          <Input
            leadingIcon={<Search />}
            placeholder={t("users.searchPlaceholder")}
            aria-label={t("users.searchPlaceholder")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <SegmentedControl
          aria-label={t("users.filter.label")}
          options={[
            { value: "active", label: t("users.filter.active") },
            { value: "all", label: t("users.filter.all") },
          ]}
          value={show}
          onChange={(value) => {
            setShow(value);
            setPage(1);
          }}
        />
        {users.data && (
          <Text as="span" variant="body-sm" tone="tertiary" className="md:ml-auto">
            {t("users.total", { count: total })}
          </Text>
        )}
      </div>
      {users.isError ? (
        <Text tone="error" role="alert">
          {t("users.loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("users.tableLabel")}
          columns={columns}
          rows={users.data?.data ?? []}
          rowKey={(user) => user.uuid}
          loading={users.isPending}
          empty={<EmptyState icon={<Users size={32} />} title={term ? t("users.noResults") : t("users.empty")} />}
          renderActions={canEdit ? rowMenu : undefined}
          actionsLabel={t("users.columns.actions")}
          pagination={{ page, pageCount: users.data?.meta.totalPages ?? 1, onPageChange: setPage }}
        />
      )}
      {actions.dialog}
    </AdminPage>
  );
}
